// src/Pages/Dashboard/ServicesTransactions.js
import React, { useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { FaSearch, FaChevronDown } from "react-icons/fa";
import SortableHeaderCell from "../../Components/SortableHeaderCell";
import FilterInput from "../../Components/FilterInput";
import { formatDate } from "../../Utils/dateFormatter";
import { useServicesTransactions } from "../../Hooks/Services/useServicesTransactions";
import { statusFilters, operationTypes, providerFilters, operationTypeTranslations } from "../../Data/servicesFilters";
import Pagination from "../../Components/Pagination";

const ServicesTransactions = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedStatusFilter, setSelectedStatusFilter] = useState("all");
  const [selectedOperationType, setSelectedOperationType] = useState("all");
  const [selectedProviderFilter, setSelectedProviderFilter] = useState("all");
  const [operationDropdownOpen, setOperationDropdownOpen] = useState(false);
  const [providerDropdownOpen, setProviderDropdownOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [ordering, setOrdering] = useState("-created_at");
  const [dateFilter, setDateFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(() => Math.max(1, parseInt(searchParams.get('page') || '1', 10)));

  const handlePageChange = (newPage) => {
    setPage(newPage);
    setSearchParams(prev => { prev.set('page', String(newPage)); return prev; }, { replace: true });
  };

  // بناء الـ queryParams للـ API
  const queryParams = useMemo(() => {
    const params = { page, ordering };

    if (appliedSearch) params.search = appliedSearch;
    if (dateFilter) params.created_at = dateFilter;
    if (dateFrom) params.created_from = dateFrom;
    if (dateTo) params.created_to = dateTo;
    if (selectedProviderFilter !== "all") params.source = selectedProviderFilter;
    if (selectedOperationType !== "all") params.operation_type = selectedOperationType;

    if (selectedStatusFilter !== "all") {
      if (selectedStatusFilter === "debt") {
        params.is_debt = true;
      } else if (selectedStatusFilter === "paid") {
        params.is_debt = false;
      } else {
        params.api_status = selectedStatusFilter;
      }
    }

    return params;
  }, [selectedStatusFilter, selectedOperationType, selectedProviderFilter, appliedSearch, ordering, dateFilter, dateFrom, dateTo, page]);

  // استدعاء hook مع queryParams
  const { transactions, total, loading, error } = useServicesTransactions(queryParams);

  const handleSort = (field, direction = "asc") => {
    setOrdering(direction === "asc" ? field : `-${field}`);
    setPage(1);
  };

  const handleApplyFilters = () => {
    setAppliedSearch(search.trim());
    setPage(1);
  };

  return (
    <div className="p-6 bg-light min-h-screen font-cairo dir-rtl">
      {/* العنوان */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-main1">عمليات التحويل</h2>
          <span className="text-gray-500 text-sm">
            إجمالي العمليات <b className="text-main2">{total}</b>
          </span>
        </div>

        {/* البحث */}
        <div className="w-full md:w-80">
          <div className="relative">
            <FilterInput
              type="text"
              placeholder="بحث في العمليات..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleApplyFilters()}
              className="w-full pl-10 pr-4"
            />
            <button
              onClick={handleApplyFilters}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-main1 transition-transform hover:scale-110"
            >
              <FaSearch />
            </button>
          </div>
        </div>
      </div>

      {/* الفلاتر */}
      <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
        <div className="flex flex-wrap gap-2">
          {statusFilters.map((f) => (
            <button
              key={f.value}
              onClick={() => { setSelectedStatusFilter(f.value); handlePageChange(1); }}
              className={`px-4 py-2 rounded-full border text-sm font-medium transition-all duration-150
                ${selectedStatusFilter === f.value ? "bg-main1 text-light border-main1" : "bg-grayish text-main1 border-gray-200 hover:bg-main1/10"}`}
            >
              {f.label}
            </button>
          ))}

          {/* فلترة المزود */}
          <div className="relative">
            <button
              onClick={() => setProviderDropdownOpen(!providerDropdownOpen)}
              className="flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium transition-all duration-150 bg-grayish text-main1 hover:bg-main1/10"
            >
              {providerFilters.find((p) => p.value === selectedProviderFilter)?.label}
              <FaChevronDown className={`transition-transform ${providerDropdownOpen ? "rotate-180" : ""}`} />
            </button>

            {providerDropdownOpen && (
              <div className="absolute right-0 mt-2 w-44 bg-light rounded-xl shadow-lg border border-gray-200 overflow-hidden z-10">
                {providerFilters.map((p) => (
                  <div
                    key={p.value}
                    onClick={() => { setSelectedProviderFilter(p.value); setProviderDropdownOpen(false); }}
                    className={`px-4 py-2 text-sm cursor-pointer transition ${selectedProviderFilter === p.value ? "bg-main1 text-light" : "hover:bg-grayish/50 text-main1"}`}
                  >
                    {p.label}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* نوع العملية */}
          <div className="relative">
            <button
              onClick={() => setOperationDropdownOpen(!operationDropdownOpen)}
              className="flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium transition-all duration-150 bg-grayish text-main1 hover:bg-main1/10"
            >
              {operationTypes.find((o) => o.value === selectedOperationType)?.label}
              <FaChevronDown className={`transition-transform ${operationDropdownOpen ? "rotate-180" : ""}`} />
            </button>

            {operationDropdownOpen && (
              <div className="absolute right-0 mt-2 w-44 bg-light rounded-xl shadow-lg border border-gray-200 overflow-hidden z-10">
                {operationTypes.map((o) => (
                  <div
                    key={o.value}
                    onClick={() => { setSelectedOperationType(o.value); setOperationDropdownOpen(false); }}
                    className={`px-4 py-2 text-sm cursor-pointer transition ${selectedOperationType === o.value ? "bg-main1 text-light" : "hover:bg-grayish/50 text-main1"}`}
                  >
                    {o.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* فلترة التاريخ */}
        <div className="flex items-center gap-2">
          <FilterInput label="تاريخ محدد" type="date" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} />
          <FilterInput label="من تاريخ" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          <FilterInput label="إلى تاريخ" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          {(dateFilter || dateFrom || dateTo) && (
            <button
              onClick={() => { setDateFilter(""); setDateFrom(""); setDateTo(""); }}
              className="text-xs rounded-xl text-main1 bg-grayish hover:bg-opacity-90 transition px-2 py-2"
            >
              مسح الفلترة
            </button>
          )}
        </div>
      </div>

      {/* الجدول */}
      <div className="overflow-x-auto rounded-2xl shadow-md bg-transparent dir-rtl">
        <table className="min-w-full text-sm text-main1 font-cairo">
          <thead>
            <tr className="bg-grayish text-main1">
              <th className="p-4 text-center">
                <SortableHeaderCell label="التاريخ" field="created_at" ordering={ordering} onSort={handleSort} align="center" ascTitle="الأقدم أولاً" descTitle="الأحدث أولاً" />
              </th>
              <th className="p-4 text-center">المزود</th>
              <th className="p-4 text-center">نوع العملية</th>
              <th className="p-4 text-center">الحالة</th>
              <th className="p-4 text-center">
                <SortableHeaderCell label="رقم المستفيد" field="recipient_number" ordering={ordering} onSort={handleSort} align="center" />
              </th>
              <th className="p-4 text-center">
                <SortableHeaderCell label="المبلغ الكلي" field="amount_requested" ordering={ordering} onSort={handleSort} align="center" />
              </th>
              <th className="p-4 text-center">
                <SortableHeaderCell label="السعر" field="price" ordering={ordering} onSort={handleSort} align="center" />
              </th>
              <th className="p-4 text-center">دين</th>
              <th className="p-4 text-center">العمولة</th>
              <th className="p-4 text-center">ملاحظة</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="10" className="text-center py-16 text-gray-500">جارٍ التحميل...</td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan="10" className="text-center py-16 text-red-500">{error}</td>
              </tr>
            ) : transactions.length === 0 ? (
              <tr>
                <td colSpan="10" className="text-center py-16 text-gray-500">لا توجد عمليات</td>
              </tr>
            ) : (
              transactions.map((t) => (
                <tr key={t.id} className="border-b hover:bg-grayish/60 transition">
                  <td className="p-4 text-center text-xs whitespace-pre-line">{formatDate(t.created_at)}</td>
                  <td className="p-4 text-center">{t.source}</td>
                  <td className="p-4 text-center">{operationTypeTranslations[t.operation_type] || t.operation_type}</td>
                  <td className="p-4 text-center">
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${t.api_status === "success" ? "bg-green-100 text-green-700" :
                      t.api_status === "failed" ? "bg-red-100 text-red-700" :
                        "bg-yellow-100 text-yellow-700"
                      }`}>{t.api_status === "success" ? "ناجحة" : t.api_status === "failed" ? "فاشلة" : "قيد الانتظار"}</span>
                  </td>
                  <td className="p-4 text-center">{t.recipient_number}</td>
                  <td className="p-4 text-center font-medium">{t.amount_requested}</td>
                  <td className="p-4 text-center font-medium">{t.price}</td>
                  <td className="p-4 text-center">
                    <span className={`px-2 py-1 rounded-full text-xs font-bold ${t.is_debt ? "bg-yellow-100 text-yellow-700" : "bg-green-100 text-green-700"}`}>
                      {t.is_debt ? "نعم" : "لا"}
                    </span>
                  </td>
                  <td className="p-4 text-center">{t.commission_amount}</td>
                  <td className="p-4 text-center">{t.note || "-"}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={page}
        totalPages={Math.ceil(total / 20)}
        onPageChange={handlePageChange}
        totalItems={total}
        itemsPerPage={20}
        loading={loading}
      />
    </div>
  );
};

export default ServicesTransactions;
